// Data transformation utilities for converting API responses to UI types
import type {
    FileListResponse,
    FileListItem,
    FileDetailResponse,
    FileEvent,
    ProcessingFile,
    ProcessingStages,
    ActivityItem,
    ErrorDetails,
    FileStatus,
    StageStatus
} from './types';
import {
    EMPTY_STAGES,
    getStageDisplayName,
    STATUS_MAPS,
    STAGES_ORDER
} from './types';

/**
 * Format ISO date string to "MMM DD, YYYY h:mm A" format
 * @param isoString - ISO 8601 date string
 * @returns Formatted date string or empty string if null
 */
export function formatDate(isoString: string | null): string {
    if (!isoString) return '';

    try {
        const date = new Date(isoString);
        const options: Intl.DateTimeFormatOptions = {
            year: 'numeric',
            month: 'short',
            day: 'numeric',
            hour: 'numeric',
            minute: '2-digit',
            hour12: true
        };
        return date.toLocaleString('en-US', options);
    } catch (error) {
        console.error('Error formatting date:', error);
        return isoString;
    }
}

/**
 * Convert bytes to human-readable file size with appropriate unit
 * @param sizeBytes - File size in bytes
 * @returns Human-readable file size (e.g., "24.8 GB", "1.5 MB")
 */
export function formatFileSize(sizeBytes: number): string {
    if (sizeBytes === 0) return '0 B';

    const gb = sizeBytes / (1024 * 1024 * 1024);
    if (gb >= 1) {
        return `${gb.toFixed(1)} GB`;
    }

    const mb = sizeBytes / (1024 * 1024);
    if (mb >= 1) {
        return `${mb.toFixed(1)} MB`;
    }

    const kb = sizeBytes / 1024;
    if (kb >= 1) {
        return `${kb.toFixed(1)} KB`;
    }

    return `${sizeBytes} B`;
}

/**
 * Map backend API file status to frontend FileStatus type
 * @param status - Backend file status
 * @param errorMessage - Error message from backend (null if no error)
 * @returns Frontend FileStatus
 */
export function mapApiStatusToFileStatus(status: string, errorMessage: string | null): FileStatus {
    if (errorMessage) {
        return 'needs-review';
    }
    return STATUS_MAPS.fileStatus(status);
}

/**
 * Map backend event status to frontend StageStatus type
 * @param status - Backend event status
 * @returns Frontend StageStatus
 */
export function mapEventStatusToStageStatus(status: string): StageStatus {
    return STATUS_MAPS.stageStatus(status);
}

/**
 * Parse backend events array and map to frontend ProcessingStages object
 * @param events - Array of FileEvent objects from backend API
 * @returns ProcessingStages object with status for each stage
 */
export function mapEventsToStages(events: FileEvent[]): ProcessingStages {
    const stages = {...EMPTY_STAGES};

    const sortedEvents = [...events].sort((a, b) => a.stage_sequence - b.stage_sequence);

    for (const event of sortedEvents) {
        const stageKey = STATUS_MAPS.stageName(event.stage_name);
        if (stageKey) {
            stages[stageKey] = mapEventStatusToStageStatus(event.status);
        }
    }

    return stages;
}

/**
 * Extract error messages from failed events for display in UI
 * @param events - Array of FileEvent objects from backend API
 * @returns Array of error message strings
 */
export function extractErrorsFromEvents(events: FileEvent[]): string[] {
    const errors: string[] = [];

    for (const event of events) {
        const status = event.status.toLowerCase();
        if (status === 'failed') {
            if (event.message) {
                errors.push(event.message);
            }

            // Check if details_json contains additional error info
            if (event.details_json && typeof event.details_json === 'object') {
                const details = event.details_json as Record<string, unknown>;
                if (typeof details.error === 'string') {
                    errors.push(details.error);
                }
                if (Array.isArray(details.errors)) {
                    errors.push(...details.errors.filter((e): e is string => typeof e === 'string'));
                }
            }
        }
    }

    return errors;
}

/**
 * Build ErrorDetails object from file detail response for error display UI
 * @param file - FileDetailResponse from backend API
 * @returns ErrorDetails object or undefined if no errors present
 */
export function extractErrorDetails(file: FileDetailResponse): ErrorDetails | undefined {
    // Only create error details if there's an error message or validation errors
    if (!file.error_message && file.validation_errors_count === 0) {
        return undefined;
    }

    const errors = extractErrorsFromEvents(file.events);

    // If we have an error_message but no errors from events, use the error_message
    if (file.error_message && errors.length === 0) {
        errors.push(file.error_message);
    }

    return {
        issuesFound: file.error_message || 'Validation issues detected',
        rowsWithIssues: file.validation_errors_count ?? 0,
        cellsWithIssues: file.validation_warnings_count ?? 0,
        errors: errors.length > 0 ? errors : ['See error details for more information']
    };
}

/**
 * Build chronological activity timeline from backend events and milestones
 * Uses event.message as primary source, with details_json as fallback/enhancement
 */
export function buildActivityTimeline(file: FileDetailResponse): ActivityItem[] {
    const activity: ActivityItem[] = [];

    for (const event of file.events) {
        if (event.message && event.message.trim() !== '') {
            activity.push({
                time: formatDate(event.event_ts),
                message: event.message,
                timestamp: event.event_ts // ISO format for reliable sorting
            });
        }
    }

    // Sort in descending order (newest first) using ISO timestamp
    // Note: Sorting by formatted time string fails in Safari (produces Invalid Date)
    activity.sort((a, b) => new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime());

    // Deduplicate by time + message
    return activity.filter((item, index, self) =>
        index === self.findIndex((t) => t.time === item.time && t.message === item.message)
    );
}

/**
 * Parse backend current_stage format and convert to frontend stage display name
 * Backend format: "summary_generation" (snake_case) → "Summary Generation"
 * @param backendStage - Stage string from backend (snake_case)
 * @returns Frontend stage display name or original string if no match
 */
function parseBackendStageText(backendStage: string): string {
    if (!backendStage) return '';

    // Convert snake_case to frontend stage display name
    const stageKey = STATUS_MAPS.stageName(backendStage);
    if (stageKey) {
        return getStageDisplayName(stageKey);
    }

    // Fallback: return original
    return backendStage;
}

/**
 * Determine current stage display text from ProcessingStages object
 * @param stages - ProcessingStages object with status for each stage
 * @returns Human-readable stage name or empty string
 */
export function getCurrentStageText(stages: ProcessingStages): string {
    // Find the last stage that's processing or failed (using STAGES_ORDER from types)
    for (let i = STAGES_ORDER.length - 1; i >= 0; i--) {
        const stageKey = STAGES_ORDER[i];
        if (stages[stageKey] === 'processing' || stages[stageKey] === 'failed') {
            return getStageDisplayName(stageKey);
        }
    }

    return '';
}

/**
 * Transform backend FileListResponse to frontend ProcessingFile array
 * @param response - FileListResponse from backend API
 * @returns Array of ProcessingFile objects for table display
 */
export function transformFileList(response: FileListResponse): ProcessingFile[] {
    return response.files.map((file: FileListItem) => {
        const status = mapApiStatusToFileStatus(file.status, file.error_message);

        // For list view, we don't have full event details
        // Initialize all stages as pending, will be loaded when user views details
        const stages = {...EMPTY_STAGES};

        // Basic activity with just the upload timestamp
        const activity: ActivityItem[] = [
            {
                time: formatDate(file.created_at),
                message: 'File uploaded',
                timestamp: file.created_at
            }
        ];

        if (file.processing_completed_at) {
            activity.push({
                time: formatDate(file.processing_completed_at),
                message: status === 'completed' ? 'Processing completed' : 'Processing completed with errors',
                timestamp: file.processing_completed_at
            });
        }

        const displayStage = file.stage_group
            ? file.stage_group.split('_').map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' ')
            : '';

        return {
            id: String(file.id),
            fileName: file.filename,
            originalFileName: file.filename,
            dateUploaded: formatDate(file.created_at),
            uploadedBy: file.uploaded_by || '',
            status,
            stage: displayStage,
            stages,
            activity,
            uploadDetails: {
                uploadedAt: `${formatDate(file.created_at)} • ${formatFileSize(file.size_bytes)}`,
                fileSize: formatFileSize(file.size_bytes),
                uploadedBy: file.uploaded_by || ''
            },
            errorDetails: file.error_message ? {
                issuesFound: file.error_message,
                rowsWithIssues: 0,
                cellsWithIssues: 0,
                errors: [file.error_message]
            } : undefined,
            errorReportFile: file.error_report_file,
            detailsLoaded: false // List view has minimal data
        };
    });
}

/**
 * Transform backend FileDetailResponse to frontend ProcessingFile object
 * @param response - FileDetailResponse from backend API
 * @returns ProcessingFile object with complete data for details panel
 */
export function transformFileDetail(response: FileDetailResponse): ProcessingFile {
    const status = mapApiStatusToFileStatus(response.status, response.error_message);
    const stages = mapEventsToStages(response.events);
    const activity = buildActivityTimeline(response);
    const errorDetails = extractErrorDetails(response);

    // Compute stage text from frontend order, fallback to backend current_stage
    const stage = getCurrentStageText(stages) ||
        (response.current_stage ? parseBackendStageText(response.current_stage) : '');

    return {
        id: String(response.id),
        fileName: response.filename,
        originalFileName: response.original_filename || response.filename,
        dateUploaded: formatDate(response.created_at),
        uploadedBy: response.uploaded_by || '',
        status,
        stage,
        stages,
        activity,
        uploadDetails: {
            uploadedAt: `${formatDate(response.created_at)} • ${formatFileSize(response.size_bytes)}`,
            fileSize: formatFileSize(response.size_bytes),
            uploadedBy: response.uploaded_by || ''
        },
        errorDetails,
        errorReportFile: response.error_report_path?.split('/').pop() || null,
        detailsLoaded: true // Detail view has full data including events and complete activity timeline
    };
}
